import { createContext, ReactNode, useContext, useState } from 'react';

import { BatchStatusEnum } from '@e-origin/shared';

export interface BatchProgressMessage {
  batchId: string;
  status: string;
  value: number;
  processedUrls: number;
  totalWebUrls: number;
}

const FULL_PERCENTAGE = 100;

interface ProgressContextType {
  progress: Map<string, BatchProgressMessage>;
  setProgressForBatch: (newProgress: BatchProgressMessage) => void;
}

const ProgressContext = createContext<ProgressContextType | undefined>(undefined);

export const ProgressProvider = ({ children }: { children: ReactNode }) => {
  const [progress, setProgress] = useState<Map<string, BatchProgressMessage>>(new Map());

  const setProgressForBatch = (newProgress: BatchProgressMessage) => {
    // when sending, the value property is a value between 0 and 1, so we need to convert it to a percentage
    const parsedPercentage = newProgress.status === BatchStatusEnum.SENDING ? FULL_PERCENTAGE * newProgress.value : newProgress.value;

    // prevent going backwards in progress. Sometimes we don't receive the messages in order
    if (parsedPercentage < progress.get(newProgress.batchId)?.value) {
      return;
    }

    if (
      parsedPercentage === FULL_PERCENTAGE ||
      [BatchStatusEnum.SUCCESS, BatchStatusEnum.FAILED].includes(newProgress.status as BatchStatusEnum) ||
      isNaN(parsedPercentage)
    ) {
      clearBatchProgress(newProgress.batchId);
      return;
    }
    setProgress((prev) => new Map(prev).set(newProgress.batchId, newProgress));
  };

  const clearBatchProgress = (batchId: string) => {
    setProgress((prev) => {
      const newProgress = new Map(prev);
      newProgress.delete(batchId);
      return newProgress;
    });
  };

  return <ProgressContext.Provider value={{ progress, setProgressForBatch }}>{children}</ProgressContext.Provider>;
};

export const useProgress = () => {
  const context = useContext(ProgressContext);
  if (context === undefined) {
    throw new Error('useProgress must be used within a ProgressProvider');
  }
  return context;
};
